import React, { useState, useEffect } from "react";

import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import Spinner from "@excalidraw/excalidraw/components/Spinner";
import {
  restoreElements,
  restoreAppState,
} from "@excalidraw/excalidraw/data/restore";
import { CaptureUpdateAction } from "@excalidraw/excalidraw";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { useAtom } from "../../app-jotai";
import {
  customFileLibraryDialogAtom,
  customFilesListAtom,
  foldersListAtom,
  collapsedFoldersAtom,
} from "../../app-jotai";
import {
  listFiles,
  getFile,
  deleteFile,
  renameFile,
  moveFile,
  listFolders,
  createFolder,
  updateFolder,
  deleteFolder,
} from "../../data/customFileApi";
import { CustomFileStorage } from "../../data/customFileStorage";

import { FileCard } from "./FileCard";
import { FolderSection } from "./FolderSection";

import "./CustomFileLibrary.scss";

interface CustomFileLibraryProps {
  excalidrawAPI: ExcalidrawImperativeAPI;
}

export const CustomFileLibrary: React.FC<CustomFileLibraryProps> = ({
  excalidrawAPI,
}) => {
  const [dialogState, setDialogState] = useAtom(customFileLibraryDialogAtom);
  const [filesList, setFilesList] = useAtom(customFilesListAtom);
  const [foldersList, setFoldersList] = useAtom(foldersListAtom);
  const [collapsedFolders, setCollapsedFolders] = useAtom(collapsedFoldersAtom);

  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFileList = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [files, folders] = await Promise.all([listFiles(), listFolders()]);
      setFilesList(files);
      setFoldersList(folders);
    } catch (err) {
      setError(
        "Failed to load files. Please check your connection and try again.",
      );
      console.error("Error loading files:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Load file list when dialog opens
  useEffect(() => {
    if (dialogState.isOpen) {
      loadFileList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogState.isOpen]);

  const handleFileSelect = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch full file data
      const fileData = await getFile(id);

      // Get current app state to use as fallback for missing properties
      const currentAppState = excalidrawAPI.getAppState();

      // Fix any corrupted appState data (e.g., collaborators as object instead of Map)
      const cleanedAppState = { ...fileData.appState };
      if (
        cleanedAppState.collaborators &&
        typeof cleanedAppState.collaborators === "object" &&
        !(cleanedAppState.collaborators instanceof Map)
      ) {
        // Remove corrupted collaborators - will be restored from currentAppState
        delete cleanedAppState.collaborators;
      }

      // Restore elements and appState properly
      const restoredElements = restoreElements(fileData.elements, null, {
        repairBindings: true,
        deleteInvisibleElements: true,
      });
      const restoredAppState = restoreAppState(
        cleanedAppState,
        currentAppState,
      );

      // Preserve the current app theme (don't let saved file theme override)
      restoredAppState.theme = currentAppState.theme;

      // Update current file FIRST (before scene update to prevent autosave creating new file)
      CustomFileStorage.setCurrentFile({
        id: fileData.id,
        name: fileData.name,
        lastSaved: fileData.lastModified,
        folderId: fileData.folderId || null,
      });

      // Update scene with proper capture action
      excalidrawAPI.updateScene({
        elements: restoredElements,
        appState: restoredAppState,
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });

      // Add binary files if any
      if (fileData.files && Object.keys(fileData.files).length > 0) {
        excalidrawAPI.addFiles(Object.values(fileData.files));
      }

      // Close dialog
      setDialogState({ isOpen: false });
    } catch (err) {
      setError("Failed to load file. Please try again.");
      console.error("Error loading file:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileDelete = async (id: string) => {
    try {
      await deleteFile(id);

      // Update file list
      setFilesList(filesList.filter((file) => file.id !== id));
    } catch (err) {
      setError("Failed to delete file. Please try again.");
      console.error("Error deleting file:", err);
    }
  };

  const handleFileRename = async (id: string, newName: string) => {
    try {
      const result = await renameFile(id, newName);

      // Update file list with new name and lastModified
      setFilesList(
        filesList.map((f) =>
          f.id === id
            ? { ...f, name: result.name, lastModified: result.lastModified }
            : f,
        ),
      );

      // Also update current file if it's the one being renamed
      const currentFile = CustomFileStorage.getCurrentFile();
      if (currentFile && currentFile.id === id) {
        CustomFileStorage.setCurrentFile({
          ...currentFile,
          name: result.name,
          lastSaved: result.lastModified,
        });
      }
    } catch (err) {
      setError("Failed to rename file. Please try again.");
      console.error("Error renaming file:", err);
      throw err; // Re-throw to let FileCard handle the error
    }
  };

  const handleCreateFolder = async () => {
    try {
      // Generate unique folder name
      let folderName = "New Folder";
      let counter = 1;
      const existingNames = new Set(foldersList.map((f) => f.name));

      while (existingNames.has(folderName)) {
        counter++;
        folderName = `New Folder ${counter}`;
      }

      const newFolder = {
        id: `folder_${Date.now()}`,
        name: folderName,
        order: foldersList.length,
      };
      await createFolder(newFolder);
      await loadFileList();
    } catch (err) {
      setError("Failed to create folder. Please try again.");
      console.error("Error creating folder:", err);
    }
  };

  const handleToggleFolderCollapse = (folderId: string) => {
    setCollapsedFolders({
      ...collapsedFolders,
      [folderId]: !collapsedFolders[folderId],
    });
  };

  const handleFolderRename = async (folderId: string, newName: string) => {
    try {
      await updateFolder(folderId, { name: newName });
      setFoldersList(
        foldersList.map((f) =>
          f.id === folderId ? { ...f, name: newName } : f,
        ),
      );
    } catch (err) {
      setError("Failed to rename folder. Please try again.");
      console.error("Error renaming folder:", err);
    }
  };

  const handleFolderDelete = async (folderId: string) => {
    try {
      await deleteFolder(folderId);
      setFoldersList(foldersList.filter((f) => f.id !== folderId));
      // Reload to get updated file list (files now have folderId = null)
      await loadFileList();
    } catch (err) {
      setError("Failed to delete folder. Please try again.");
      console.error("Error deleting folder:", err);
    }
  };

  const handleMoveToFolder = async (
    fileId: string,
    folderId: string | null,
  ) => {
    try {
      // Use PATCH so only folderId changes — never touches the drawing elements
      await moveFile(fileId, folderId);

      // Update local state
      setFilesList(
        filesList.map((f) => (f.id === fileId ? { ...f, folderId } : f)),
      );

      // If this is the currently open file, update its tracking too
      const currentFile = CustomFileStorage.getCurrentFile();
      if (currentFile.id === fileId) {
        CustomFileStorage.setCurrentFile({ ...currentFile, folderId });
      }
    } catch (err) {
      setError("Failed to move file. Please try again.");
      console.error("Error moving file:", err);
    }
  };

  // Filter files based on search query
  const filteredFiles = filesList.filter((file) =>
    file.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Organize files by folder
  const filesByFolder = filteredFiles.reduce((acc, file) => {
    const folderId = file.folderId || "uncategorized";
    if (!acc[folderId]) {
      acc[folderId] = [];
    }
    acc[folderId].push(file);
    return acc;
  }, {} as Record<string, typeof filteredFiles>);

  if (!dialogState.isOpen) {
    return null;
  }

  return (
    <Dialog
      onCloseRequest={() => setDialogState({ isOpen: false })}
      title={
        <div className="CustomFileLibrary__title">
          <span>My Files</span>
          <div style={{ display: "flex", gap: "8px" }}>
            <FilledButton
              size="medium"
              variant="outlined"
              color="muted"
              onClick={() => {
                CustomFileStorage.clearCurrentFile();
                excalidrawAPI.resetScene();
                setDialogState({ isOpen: false });
              }}
              label="New File"
            >
              + File
            </FilledButton>
            <FilledButton
              size="medium"
              variant="outlined"
              color="muted"
              onClick={handleCreateFolder}
              label="New Folder"
            >
              + Folder
            </FilledButton>
          </div>
        </div>
      }
      size="wide"
    >
      <div className="CustomFileLibrary">
        <div className="CustomFileLibrary__search">
          <input
            type="text"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="CustomFileLibrary__search-input"
          />
        </div>

        {isLoading && (
          <div className="CustomFileLibrary__loading">
            <Spinner />
            <p>Loading files...</p>
          </div>
        )}

        {error && !isLoading && (
          <div className="CustomFileLibrary__error">
            <p>{error}</p>
            <FilledButton size="medium" onClick={loadFileList}>
              Retry
            </FilledButton>
          </div>
        )}

        {!isLoading && !error && filteredFiles.length === 0 && (
          <div className="CustomFileLibrary__empty">
            {searchQuery ? (
              <p>No files found matching "{searchQuery}"</p>
            ) : (
              <p>
                No files yet. Start drawing and your work will be saved
                automatically!
              </p>
            )}
          </div>
        )}

        {!isLoading && !error && filteredFiles.length > 0 && (
          <div className="CustomFileLibrary__content">
            {foldersList.map((folder) => {
              const folderFiles = filesByFolder[folder.id] || [];
              if (folderFiles.length === 0 && searchQuery) {
                return null;
              }

              return (
                <FolderSection
                  key={folder.id}
                  folder={folder}
                  isCollapsed={collapsedFolders[folder.id] || false}
                  onToggleCollapse={handleToggleFolderCollapse}
                  onRename={handleFolderRename}
                  onDelete={handleFolderDelete}
                  fileCount={folderFiles.length}
                >
                  {folderFiles.length > 0 ? (
                    folderFiles.map((file) => (
                      <FileCard
                        key={file.id}
                        file={file}
                        folders={foldersList}
                        onSelect={handleFileSelect}
                        onDelete={handleFileDelete}
                        onRename={handleFileRename}
                        onMoveToFolder={handleMoveToFolder}
                      />
                    ))
                  ) : (
                    <div className="CustomFileLibrary__empty-folder">
                      Empty folder
                    </div>
                  )}
                </FolderSection>
              );
            })}

            {filesByFolder.uncategorized &&
              filesByFolder.uncategorized.length > 0 && (
                <FolderSection
                  folder={{
                    id: "uncategorized",
                    name: "Uncategorized",
                    createdAt: "",
                    lastModified: "",
                    order: foldersList.length,
                  }}
                  isCollapsed={collapsedFolders.uncategorized || false}
                  onToggleCollapse={handleToggleFolderCollapse}
                  onRename={() => {}} // No-op for uncategorized
                  onDelete={() => {}} // No-op for uncategorized
                  showActions={false} // Hide rename/delete for uncategorized
                  fileCount={filesByFolder.uncategorized.length}
                >
                  {filesByFolder.uncategorized.map((file) => (
                    <FileCard
                      key={file.id}
                      file={file}
                      folders={foldersList}
                      onSelect={handleFileSelect}
                      onDelete={handleFileDelete}
                      onRename={handleFileRename}
                      onMoveToFolder={handleMoveToFolder}
                    />
                  ))}
                </FolderSection>
              )}
          </div>
        )}
      </div>
    </Dialog>
  );
};
